import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { bindOrderNo } from '@/api/member';
import type { Member } from '@shared/api.interface';
import { isValidOrderNo } from '@shared/input-validation';

interface OrderNoBindFormProps {
  passcode: string;
  member: Member;
  continueLabel: string;
  onDone: () => void;
}

/** 批量码首次激活后出现：选填小红书订单号，方便以后忘记兑换码时由客服找回。不填也能继续。 */
export function OrderNoBindForm({ passcode, member, continueLabel, onDone }: OrderNoBindFormProps) {
  const [orderNo, setOrderNo] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const quotaText = member.quota_mode === 'total'
    ? `可领取 ${member.total_quota ?? 0} 个主题`
    : `每月可领取 ${member.monthly_quota} 个主题`;

  const handleSave = async () => {
    const value = orderNo.trim();
    if (!value) {
      onDone();
      return;
    }
    if (!isValidOrderNo(value)) {
      setError('订单号只能包含字母和数字，请从小红书「我的订单」里复制');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await bindOrderNo({ passcode, order_no: value });
      onDone();
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(typeof message === 'string' && message ? message : '保存失败，可以先跳过，稍后联系客服补充');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-8 w-8 shrink-0 text-claimed" aria-hidden />
        <div className="min-w-0">
          <p className="text-lg font-semibold leading-tight">兑换码已激活</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {member.member_type} · {quotaText}
            <br />
            有效期至 {member.expiry_date}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <label htmlFor="bind-order-no" className="text-sm font-medium">小红书订单号（选填）</label>
        <Input
          id="bind-order-no"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={64}
          placeholder="在小红书「我的订单」里复制"
          value={orderNo}
          aria-invalid={Boolean(error)}
          onChange={(e) => {
            setOrderNo(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          className="h-12 rounded-xl font-mono"
          disabled={isSaving}
        />
        <p className="text-xs text-muted-foreground">填写后，忘记兑换码时客服可以用订单号帮你找回</p>
        {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      </div>
      <div className="space-y-2">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="h-12 w-full rounded-xl bg-cta text-base font-semibold text-cta-foreground hover:bg-cta-hover"
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {orderNo.trim() ? `保存并${continueLabel}` : continueLabel}
        </Button>
        {orderNo.trim() && (
          <Button variant="ghost" onClick={onDone} disabled={isSaving} className="h-11 w-full rounded-xl text-muted-foreground">
            跳过
          </Button>
        )}
      </div>
    </div>
  );
}
